import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get, set } from 'firebase/database';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, TrendingUp, Send, CheckCircle, Coins } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import './MaintenancePreview.css';
import { BaseComponentImpl } from '../../../types/implementations';
import { ComponentLifetime, MaintenanceTemplate, TemplatesByType, getLifetimeForDirection } from '../TemplatesEditor/types';
import { MaintenanceIntervalType } from '../../../types/Enums';
import { Building } from '../../../types/protocols';
import { BaseType, COMPONENT_TO_BASE_TYPE } from '../../../types/Enums';
import { translate } from '../../../types/EnumTranslations';
import { SERVICE_COLORS } from '../../../Helpers/chartColors';
import { 
    generateYearlyPlansForBuilding, 
    YearlyActivity,
    ComponentMaintenanceData,
    MaintenancePlan,
    sortActivitiesByDate
} from '../../../Helpers/maintenanceCostUtils';
import MaintenanceActivityList from '../../../Components/MaintenanceActivityList';
import { Helmet } from 'react-helmet-async';

interface MaintenanceDraft extends MaintenancePlan {
    // MaintenanceDraft uses the same structure as MaintenancePlan
}

interface MaintenanceProposal {
    components: {
        [componentKey: string]: ComponentMaintenanceData;
    };
    metadata: {
        savedAt: string;
        buildingKey: string;
    };
    submission: {
        submittedBy: string;
        submittedAt: string;
        status: 'pending' | 'approved' | 'rejected';
        reviewedBy?: string;
        reviewedAt?: string;
        rejectionComment?: string;
    };
}

interface MaintenancePreviewProps {
    showBackButton?: boolean;
    onClose?: () => void;
}

// YearlyActivity interface is imported from maintenanceCostUtils

const MaintenancePreview: React.FC<MaintenancePreviewProps> = ({ showBackButton = true, onClose }) => {
    const { buildingKey } = useParams<{ buildingKey: string }>();
    const navigate = useNavigate();
    const [building, setBuilding] = useState<Building | null>(null);
    const [maintenanceDraft, setMaintenanceDraft] = useState<MaintenanceDraft | null>(null);
    const [componentLifetimes, setComponentLifetimes] = useState<Record<string, ComponentLifetime>>({});
    const [maintenanceTemplates, setMaintenanceTemplates] = useState<TemplatesByType>({});
    const [componentConditions, setComponentConditions] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [yearsToShow, setYearsToShow] = useState<number>(10);
    const [yearlyPlans, setYearlyPlans] = useState<YearlyActivity[]>([]);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [submissionSuccess, setSubmissionSuccess] = useState<boolean>(false);

    const db = getDatabase();
    const auth = getAuth();

    // Submit plan for review
    const handleSubmitPlan = async (): Promise<void> => {
        if (!maintenanceDraft || !buildingKey) return;

        // Validate that the draft has components with maintenance activities
        if (!maintenanceDraft.components || Object.keys(maintenanceDraft.components).length === 0) {
            alert('Underhållsplanen innehåller inga komponenter med underhållsaktiviteter. Går tillbaka till utkastet för att lägga till underhållsaktiviteter.');
            navigate(`/maintenanceDrafts/${buildingKey}`);
            return;
        }

        // Log component details for debugging
        const componentCount = Object.keys(maintenanceDraft.components).length;
        const totalActivities = Object.values(maintenanceDraft.components).reduce((total, comp) => {
            return total + Object.keys(comp.maintenanceActivities || {}).length;
        }, 0);
        
        console.log(`🔍 Submitting maintenance plan with ${componentCount} components and ${totalActivities} total activities`);
        console.log('Components:', Object.keys(maintenanceDraft.components));

        setSubmitting(true);
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }

            const proposal: MaintenanceProposal = {
                ...maintenanceDraft,
                submission: {
                    submittedBy: user.uid,
                    submittedAt: new Date().toISOString(),
                    status: 'pending'
                }
            };

            // First, save to proposed node
            await set(ref(db, `maintenance/proposed/${buildingKey}`), proposal);
            
            // Verify the proposal was saved successfully
            const proposalSnapshot = await get(ref(db, `maintenance/proposed/${buildingKey}`));
            if (!proposalSnapshot.exists()) {
                throw new Error('Failed to save proposal - verification failed');
            }
            
            // Only after successful verification, remove the draft
            await set(ref(db, `maintenance/drafts/${buildingKey}`), null);

            setSubmissionSuccess(true);
            
            // Redirect to review page after a short delay
            setTimeout(() => {
                navigate(`/maintenanceReview/${buildingKey}`);
            }, 2000);

        } catch (error) {
            console.error('Error submitting plan:', error);
            alert('Ett fel uppstod vid inlämning av förslaget. Försök igen.');
        } finally {
            setSubmitting(false);
        }
    };

    // Cost Projection Chart Component
    const CostProjectionChart: React.FC<{ yearlyPlans: YearlyActivity[] }> = ({ yearlyPlans }) => {
        // Get all unique BaseTypes across all years
        const allBaseTypes = new Set<string>();
        yearlyPlans.forEach(plan => {
            Object.keys(plan.costsByBaseType).forEach(baseType => {
                allBaseTypes.add(baseType);
            });
        });
        const baseTypeKeys = Array.from(allBaseTypes).sort();

        const chartData = yearlyPlans.map(plan => {
            const data: any = {
                year: plan.year.toString(),
                totalCost: plan.totalCost
            };
            
            // Add each BaseType as a separate data key
            baseTypeKeys.forEach(baseType => {
                data[baseType] = plan.costsByBaseType[baseType] || 0;
            });
            
            return data;
        });

        return (
            <div className="cost-chart-section">
                <div className="cost-chart-card">
                    <div className="chart-header">
                        <h3 className="chart-title">
                            <TrendingUp size={20} className="me-2" />
                            Kostnadsprognos
                        </h3>
                        <p className="chart-subtitle">
                            Planerade kostnader per år för de kommande {yearsToShow} åren
                        </p>
                    </div>
                    <div className="chart-container">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={chartData}
                                margin={{ top: 20, right: 30, left: 30, bottom: 60 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="year"
                                    angle={-45}
                                    textAnchor="end"
                                    height={60}
                                    tickMargin={20}
                                    interval="preserveStartEnd"
                                />
                                <YAxis 
                                    label={{ value: 'Kostnad (kr)', angle: -90, position: 'insideLeft', offset: -15 }}
                                    tickFormatter={(value) => value.toLocaleString('sv-SE')}
                                />
                                <Tooltip
                                    formatter={(value: number, name: string) => [
                                        `${value.toLocaleString('sv-SE')} kr`, 
                                        name === 'totalCost' ? 'Total kostnad' : name
                                    ]}
                                    labelFormatter={(label: string) => `År: ${label}`}
                                />
                                <Legend />
                                {baseTypeKeys.map((baseType, index) => (
                                    <Bar
                                        key={baseType}
                                        dataKey={baseType}
                                        name={baseType}
                                        stackId="costs"
                                        fill={SERVICE_COLORS[index % SERVICE_COLORS.length]}
                                        radius={index === baseTypeKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        );
    };

    // Cost calculation is now handled by the shared utility

    // Load all required data
    const loadData = async (): Promise<void> => {
        try {
            setLoading(true);

            const [summarySnapshot, modelSnapshot, draftSnapshot, lifetimesSnapshot, templatesSnapshot, conditionsSnapshot] = await Promise.all([
                get(ref(db, `buildingsSummary/${buildingKey!}`)),
                get(ref(db, `buildings/${buildingKey!}`)),
                get(ref(db, `maintenance/drafts/${buildingKey!}`)),
                get(ref(db, 'maintenance/componentLifetimes')),
                get(ref(db, 'maintenance/templates')),
                get(ref(db, `componentConditions/${buildingKey!}`))
            ]);

            // Load building data
            if (summarySnapshot.exists() || modelSnapshot.exists()) {
                const building: Building = {
                    key: buildingKey!,
                    summary: summarySnapshot.exists() ? summarySnapshot.val() : { key: buildingKey! },
                    model: modelSnapshot.exists() ? modelSnapshot.val() : { key: buildingKey! }
                };
                setBuilding(building);
            }

            // Load component conditions
            if (conditionsSnapshot.exists()) {
                const data = conditionsSnapshot.val() as Record<string, any>;
                setComponentConditions(data);
            } else {
                setComponentConditions({});
            }

            // Load maintenance draft
            if (draftSnapshot.exists()) {
                const draftData = draftSnapshot.val();
                if (draftData.components && draftData.metadata) {
                    // New structure
                    setMaintenanceDraft(draftData);
                } else {
                    // Old structure - convert to new structure
                    const convertedDraft: MaintenanceDraft = {
                        components: {},
                        metadata: {
                            savedAt: draftData.savedAt || new Date().toISOString(),
                            buildingKey: draftData.buildingKey || buildingKey!
                        }
                    };
                    
                    // Convert replacement dates
                    if (draftData.replacementDates) {
                        Object.entries(draftData.replacementDates).forEach(([componentKey, value]) => {
                            const year = typeof value === 'string' ? parseInt(value, 10) : (value as number);
                            if (!convertedDraft.components[componentKey]) {
                                convertedDraft.components[componentKey] = { maintenanceActivities: {} };
                            }
                            convertedDraft.components[componentKey].replacementDate = year;
                        });
                    }
                    
                    // Convert maintenance years and intervals
                    if (draftData.maintenanceYears || draftData.maintenanceIntervals) {
                        const allKeys = new Set([
                            ...Object.keys(draftData.maintenanceYears || {}),
                            ...Object.keys(draftData.maintenanceIntervals || {})
                        ]);
                        
                        allKeys.forEach(key => {
                            const lastUnderscoreIndex = key.lastIndexOf('_');
                            if (lastUnderscoreIndex > 0) {
                                const componentKey = key.substring(0, lastUnderscoreIndex);
                                const templateId = key.substring(lastUnderscoreIndex + 1);
                                
                                if (!convertedDraft.components[componentKey]) {
                                    convertedDraft.components[componentKey] = { maintenanceActivities: {} };
                                }
                                
                                if (!convertedDraft.components[componentKey].maintenanceActivities[templateId]) {
                                    convertedDraft.components[componentKey].maintenanceActivities[templateId] = {
                                        nextDate: new Date().toISOString().split('T')[0],
                                        intervalType: MaintenanceIntervalType.years,
                                        intervalValue: 1,
                                        lastUpdated: new Date().toISOString()
                                    };
                                }
                                
                                // Set next date
                                if (draftData.maintenanceYears?.[key]) {
                                    const year = typeof draftData.maintenanceYears[key] === 'string' 
                                        ? parseInt(draftData.maintenanceYears[key], 10) 
                                        : draftData.maintenanceYears[key];
                                    convertedDraft.components[componentKey].maintenanceActivities[templateId].nextDate = `${year}-01-01`;
                                }
                                
                                // Set interval
                                if (draftData.maintenanceIntervals?.[key]) {
                                    const interval = typeof draftData.maintenanceIntervals[key] === 'string' 
                                        ? parseInt(draftData.maintenanceIntervals[key], 10) 
                                        : draftData.maintenanceIntervals[key];
                                    convertedDraft.components[componentKey].maintenanceActivities[templateId].intervalValue = interval;
                                }
                            }
                        });
                    }
                    
                    setMaintenanceDraft(convertedDraft);
                }
            }

            // Load component lifetimes
            if (lifetimesSnapshot.exists()) {
                const data = lifetimesSnapshot.val() as Record<string, ComponentLifetime>;
                setComponentLifetimes(data);
            }

            // Load maintenance templates
            if (templatesSnapshot.exists()) {
                const data = templatesSnapshot.val() as Record<string, Record<string, any>>;
                const templatesByType: TemplatesByType = {};
                
                Object.entries(data).forEach(([componentType, componentTemplates]) => {
                    if (componentTemplates && typeof componentTemplates === 'object') {
                        templatesByType[componentType] = Object.entries(componentTemplates).map(([id, template]: [string, any]) => ({
                            id,
                            name: template.name || '',
                            description: template.description || '',
                            interval_type: template.interval_type || 'years',
                            interval_value: template.interval_value || 1,
                            cost: template.cost ?? 0,
                            cost_unit: template.cost_unit ?? 'per_sqm',
                            directionalIntervals: template.directionalIntervals || undefined
                        }));
                    }
                });
                setMaintenanceTemplates(templatesByType);
            }

        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Generate yearly plans using shared utility
    const generateYearlyPlans = (): void => {
        if (!building?.model?.structure?.components || !maintenanceDraft || !componentLifetimes || !maintenanceTemplates) {
            return;
        }

        const buildingName = building.summary?.name || 'Namnlös byggnad';
        
        const yearlyPlans = generateYearlyPlansForBuilding({
            building,
            plan: maintenanceDraft,
            componentLifetimes,
            maintenanceTemplates,
            componentConditions,
            yearsToShow,
            buildingName
        });

        setYearlyPlans(yearlyPlans);
    };

    useEffect(() => {
        if (buildingKey) {
            loadData();
        }
    }, [buildingKey]);

    useEffect(() => {
        if (!loading) {
            generateYearlyPlans();
        }
    }, [loading, yearsToShow, maintenanceDraft, componentLifetimes, maintenanceTemplates, componentConditions]);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </div>
                    <h5 className="text-muted">Laddar förhandsvisning...</h5>
                </div>
            </div>
        );
    }

    if (!building) {
        return (
            <div className="alert alert-warning">
                <h4>Byggnad hittades inte</h4>
                <p>Kunde inte ladda byggnadsinformation för nyckel: {buildingKey!}</p>
            </div>
        );
    }

    if (!maintenanceDraft) {
        return (
            <div className="alert alert-info">
                <h4>Ingen underhållsplan hittades</h4>
                <p>Inga sparade underhållsplaner hittades för denna byggnad.</p>
            </div>
        );
    }

    const totalActivities = yearlyPlans.reduce((total, year) => total + year.activities.length, 0);
    const totalCost = yearlyPlans.reduce((total, year) => total + year.totalCost, 0);

    return (
        <div className="maintenance-preview" style={{padding: '40px'}}>
            <Helmet>
                <title>{`Förhandsgranska underhållsplan - ${building?.summary?.name || 'Byggnad'} - Molnfastigheter`}</title>
            </Helmet>
            {/* Sticky Submit Button */}
            <div className="sticky-submit-container">
                {submissionSuccess ? (
                    <div className="alert alert-success mb-0 d-inline-flex align-items-center">
                        <CheckCircle size={20} className="me-2" />
                        Plan inlämnad!
                    </div>
                ) : (
                    <button 
                        className="btn btn-primary"
                        onClick={handleSubmitPlan}
                        disabled={submitting || !maintenanceDraft}
                    >
                        {submitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Lämnar in...
                            </>
                        ) : (
                            <>
                                <Send size={20} className="me-2" />
                                Lämna in förslag
                            </>
                        )}
                    </button>
                )}
            </div>

            {/* Top Navigation Buttons */}
            <div className="top-navigation">
                {showBackButton && (
                    <div className="back-button-container">
                        <button 
                            className="btn btn-outline-secondary"
                            onClick={() => navigate(`/building/${buildingKey}`)}
                        >
                            <ArrowLeft size={20} className="me-2" />
                            Tillbaka till byggnad
                        </button>
                    </div>
                )}
            </div>

            {/* Header */}
            <div className="header-section">
                <div className="header-content" style={{marginTop: '0px', marginBottom: '30px'}} >
                    <h1 className="display-6 fw-bold text-dark">
                        Förhandsvisning: {building.summary?.name || 'Namnlös byggnad'}
                    </h1>
                    <p className="lead text-muted">
                        Planerade underhållsaktiviteter och ersättningar för de kommande {yearsToShow} åren
                    </p>
                </div>
            </div>

            {/* Controls */}
            <div className="controls-section" style={{padding: '20px'}}>
                <div className="row align-items-center">
                    <div className="col-md-6">
                        <div className="d-flex align-items-center">
                            <label htmlFor="yearsToShow" className="form-label me-3 mb-0">
                                Visa plan för:
                            </label>
                            <select 
                                id="yearsToShow"
                                className="form-select"
                                value={yearsToShow}
                                onChange={(e) => setYearsToShow(parseInt(e.target.value, 10))}
                                style={{ width: 'auto' }}
                            >
                                <option value={10}>10 år</option>
                                <option value={25}>25 år</option>
                                <option value={50}>50 år</option>
                                <option value={100}>100 år</option>
                            </select>
                        </div>
                    </div>
                    <div className="col-md-6 text-end">
                        <div className="stats-summary">
                            <span className="badge bg-primary me-2">
                                <Calendar size={16} className="me-1" />
                                {yearsToShow} år
                            </span>
                            <span className="badge bg-success me-2">
                                {totalActivities} aktiviteter
                            </span>
                            <span className="badge bg-warning">
                                <Coins size={16} className="me-1" />
                                {Math.round(totalCost).toLocaleString('sv-SE')} kr
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Cost Projection Chart */}
            {yearlyPlans.length > 0 && (
                <CostProjectionChart yearlyPlans={yearlyPlans} />
            )}

            {/* Yearly Plans */}
            <div className="yearly-plans">
                {yearlyPlans.map((yearPlan) => (
                    <div key={yearPlan.year} className="year-card">
                        <div className="year-header">
                            <h3 className="year-title">{yearPlan.year}</h3>
                            <div className="year-stats">
                                <span className="activity-count me-3">
                                    {yearPlan.activities.length} aktivitet{yearPlan.activities.length !== 1 ? 'er' : ''}
                                </span>
                                {yearPlan.totalCost > 0 && (
                                    <span className="year-cost">
                                        <Coins size={16} className="me-1" />
                                        {Math.round(yearPlan.totalCost).toLocaleString('sv-SE')} kr
                                    </span>
                                )}
                            </div>
                        </div>
                        
                        {yearPlan.activities.length > 0 ? (
                            <MaintenanceActivityList activities={sortActivitiesByDate(yearPlan.activities)} />
                        ) : (
                            <div className="no-activities">
                                <span className="text-muted">Inga planerade aktiviteter</span>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default MaintenancePreview;
